import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { BASE_URL } from '../apiConfig'

const fetchData = async (url, jwtBearer) => {
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${jwtBearer}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Network response was not ok: ${response.status} ${errorText}`);
  }

  return await response.json();
};

export const fetchLeadsData = createAsyncThunk(
  'leads/fetchLeadsData',
  async ({ jwtBearer, page, pageSize, filters }, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams({
        page,
        page_size: pageSize,
        ...filters
      }).toString();

      const result = await fetchData(`${BASE_URL}pipeline/leads/data/?${queryParams}`, jwtBearer);

      return result;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        window.location.reload();
      }
      console.error('Fetch error:', error);
      return rejectWithValue(error.response ? error.response.data : error.message);
    }
  }
);


export const fetchFilteredLeadsData = createAsyncThunk(
  'leads/fetchFilteredLeadsData',
  async ({ jwtBearer, filters }, thunkAPI) => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const result = await fetchData(`${BASE_URL}pipeline/leads/data/?${queryParams}`, jwtBearer);
      return result;
    } catch (error) {
      console.error('Fetch error:', error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);


export const fetchConvertedLeadsData = createAsyncThunk(
  'leads/fetchConvertedLeadsData',
  async ({ jwtBearer, page, pageSize, filters }, thunkAPI) => {
    try {
      const queryParams = new URLSearchParams({
        page,
        page_size: pageSize,
        ...filters
      }).toString();

      const result = await fetchData(`${BASE_URL}pipeline/converted-leads/data/?${queryParams}`, jwtBearer);
      return result;
    } catch (error) {
      console.error('Fetch error:', error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);


export const fetchFilteredConvertedLeadsData = createAsyncThunk(
  'leads/fetchFilteredConvertedLeadsData',
  async ({ jwtBearer, filters }, thunkAPI) => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const result = await fetchData(`${BASE_URL}pipeline/converted-leads/data/?${queryParams}`, jwtBearer);
      return result;
    } catch (error) {
      console.error('Fetch error:', error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const fetchNonConvertedLeadsData = createAsyncThunk(
  'leads/fetchNonConvertedLeadsData',
  async ({ jwtBearer, page, pageSize, filters }, thunkAPI) => {
    try {
      const queryParams = new URLSearchParams({
        page,
        page_size: pageSize,
        ...filters
      }).toString();

      const result = await fetchData(`${BASE_URL}pipeline/non-converted-leads/data/?${queryParams}`, jwtBearer);
      return result;
    } catch (error) {
      console.error('Fetch error:', error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);


export const fetchFilteredNonConvertedLeadsData = createAsyncThunk(
  'leads/fetchFilteredNonConvertedLeadsData',
  async ({ jwtBearer, filters }, thunkAPI) => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const result = await fetchData(`${BASE_URL}pipeline/non-converted-leads/data/?${queryParams}`, jwtBearer);
      return result;
    } catch (error) {
      console.error('Fetch error:', error);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

const leadsSlice = createSlice({
  name: 'leadsReports',
  initialState: {
    leadsData: [],
    convertedLeadsData: [],
    nonConvertedLeadsData: [],
    filteredLeadsData: [],
    filteredConvertedLeadsData: [],
    nonFilteredConvertedLeadsData: [],
    totalCountLeads: 0,
    totalCountConvertedLeads: 0,
    totalCountNonConvertedLeads: 0,
    isLoadingLeads: false,
    isLoadingConvertedLeads: false,
    isLoadingNonConvertedLeads: false,
    isLoadingFilteredLeads: false,
    isLoadingFilteredConvertedLeads: false,
    isLoadingNonFilteredConvertedLeads: false,
    errorLeads: null,
    errorConvertedLeads: null,
    errorNonConvertedLeads: null,
    errorFilteredLeads: null,
    errorFilteredCovertedLeads: null,
    errorNonFilteredCovertedLeads: null
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
     
      .addCase(fetchLeadsData.pending, (state) => {
        state.isLoadingLeads = true;
        state.errorLeads = null;
      })
      .addCase(fetchLeadsData.fulfilled, (state, action) => {
        state.isLoadingLeads = false;

        state.leadsData = action.payload.leads_data || [];
        console.log("state.leadsData", state.leadsData)

        state.totalCountLeads = action.payload.lead_total_count || 0;
        console.log(" state.totalCountLeads", state.totalCountLeads)
        
        state.updatedOn=action.payload.updated_on || 0;
        console.log("state.updatedOn",state.updatedOn)
      })
      .addCase(fetchLeadsData.rejected, (state, action) => {
        state.isLoadingLeads = false;
        state.errorLeads = action.payload || 'An error occurred while fetching auto repeat data';
      })



      .addCase(fetchConvertedLeadsData.pending, (state) => {
        state.isLoadingConvertedLeads= true;
        state.errorConvertedLeads = null;
      })
      .addCase(fetchConvertedLeadsData.fulfilled, (state, action) => {
        state.isLoadingConvertedLeads= false;
        state.convertedLeadsData = action.payload.converted_leads_data || [];
        console.log("state.convertedLeadsData", state.convertedLeadsData)
        state.totalCountConvertedLeads = action.payload.total_converted_count || 0;
        console.log("state.totalCountConvertedLeads", state.totalCountConvertedLeads)
      })
      .addCase(fetchConvertedLeadsData.rejected, (state, action) => {
        state.isLoadingConvertedLeads= false;
        state.errorConvertedLeads = action.payload || 'An error occurred while fetching full appraisal data';
      })


      // Non Converted leads data
      .addCase(fetchNonConvertedLeadsData.pending, (state) => {
        state.isLoadingNonConvertedLeads = true;
        state.errorNonConvertedLeads = null;
      })
      .addCase(fetchNonConvertedLeadsData.fulfilled, (state, action) => {
        state.isLoadingNonConvertedLeads = false;
        state.nonConvertedLeadsData = action.payload.non_converted_leads_data || [];
        console.log("state.nonConvertedLeadsData", state.nonConvertedLeadsData)
        state.totalCountNonConvertedLeads = action.payload.total_non_converted_count || 0;
        console.log("state.totalCountNonConvertedLeads", state.totalCountNonConvertedLeads)
      })
      .addCase(fetchNonConvertedLeadsData.rejected, (state, action) => {
        state.isLoadingNonConvertedLeads = false;
        state.errorNonConvertedLeads = action.payload || 'An error occurred while fetching non converted leads data';
      })

      // Filtered leads data
      .addCase(fetchFilteredLeadsData.pending, (state) => {
        state.isLoadingFilteredLeads = true;
        state.errorFilteredLeads = null;
      })
      .addCase(fetchFilteredLeadsData.fulfilled, (state, action) => {
        state.isLoadingFilteredLeads = false;
        state.filteredLeadsData = action.payload.leads_data || [];
        console.log("state.filteredLeadsData ", state.filteredLeadsData)
      })
      .addCase(fetchFilteredLeadsData.rejected, (state, action) => {
        state.isLoadingFilteredLeads = false;
        state.errorFilteredLeads = action.payload || 'An error occurred while fetching filtered auto repeat data';
      })



      // Filtered converted leads data
      .addCase(fetchFilteredConvertedLeadsData.pending, (state) => {
        state.isLoadingFilteredConvertedLeads = true;
        state.errorFilteredCovertedLeads = null;
      })
      .addCase(fetchFilteredConvertedLeadsData.fulfilled, (state, action) => {
        state.isLoadingFilteredConvertedLeads = false;
        state.filteredConvertedLeadsData = action.payload.converted_leads_data || [];
        console.log("state.filteredConvertedLeadsData", state.filteredConvertedLeadsData);
      })
      .addCase(fetchFilteredConvertedLeadsData.rejected, (state, action) => {
        state.isLoadingFilteredConvertedLeads = false;
        state.errorFilteredCovertedLeads = action.payload || 'An error occurred while fetching filtered full appraisal data';
      })

      // filtered non converted leads data
      .addCase(fetchFilteredNonConvertedLeadsData.pending, (state) => {
        state.isLoadingFilteredNonConvertedLeads = true;
        state.errorFilteredNonConvertedLeads = null;
      })
      .addCase(fetchFilteredNonConvertedLeadsData.fulfilled, (state, action) => {
        state.isLoadingFilteredNonConvertedLeads = false;
        state.filteredNonConvertedLeadsData = action.payload.non_converted_leads_data || [];
        console.log("state.filteredNonConvertedLeadsData", state.filteredNonConvertedLeadsData);
      })
      .addCase(fetchFilteredNonConvertedLeadsData.rejected, (state, action) => {
        state.isLoadingFilteredNonConvertedLeads = false;
        state.errorFilteredNonConvertedLeads = action.payload || 'An error occurred while fetching filtered non converted leads data';
      })
  },
})

export default leadsSlice.reducer
